import { PrivacyDropdown } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { useEffectOnce } from 'react-use';

import { useConfig } from 'src/context/config';
import { Page, PageSection, useTrackingClient } from 'src/context/tracking';

import translations from './ProfileVisibility.vocab';

export interface ProfileVisibilityProps {
  onChange: (value: any) => void;
  privacyLevelMessage?: string;
  value?: any;
}

const ProfileVisibility = (props: ProfileVisibilityProps) => {
  const { config } = useConfig();
  const zone = config.ZONE;
  const { onChange, privacyLevelMessage, value } = props;

  const client = useTrackingClient();
  const { t } = useTranslations(translations);

  useEffectOnce(() => {
    client.trackLink({
      link: 'profile_section_displayed',
      eventName: ['profile_section_displayed'],
      linkName: 'profile_section_displayed',
      currentPage: Page.REVIEW_PAGE,
      currentPageSection: PageSection.PROFILE_VISIBILITY,
      pageTitle: Page.REVIEW_PAGE,
      currentPageSectionState: value ?? 'not set',
    });
  });

  useEffect(() => {
    if (privacyLevelMessage) {
      client.trackLink({
        link: 'candidate-did-not-select-profile-visibility',
        pageTitle: Page.REVIEW_PAGE,
        currentPage: Page.REVIEW_PAGE,
        eventName: ['submit_error_viewed'],
      });
    }
  }, [privacyLevelMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      id="privacyDropdown"
      data-testid="privacy-dropdown"
      style={{ background: '#EBE6EE' }}
      paddingX="gutter"
      paddingY="large"
    >
      <Stack space="medium">
        <Heading level="3">{t('Be found by other employers')}</Heading>
        <PrivacyDropdown
          level={value}
          valid={!privacyLevelMessage}
          label=""
          onChange={onChange}
          message={privacyLevelMessage}
          zone={zone}
          isUnifiedSite={true}
        />
      </Stack>
    </Box>
  );
};

export const ProfileVisibilityInput = ({
  control,
  privacyLevelMessage,
}: {
  control: Control<any>;
  privacyLevelMessage?: string;
}) => {
  const { t } = useTranslations(translations);

  return (
    <Controller
      control={control}
      render={({ field }) => (
        <ProfileVisibility
          {...field}
          privacyLevelMessage={privacyLevelMessage}
        />
      )}
      name="profilePrivacyLevel"
      rules={{
        required: {
          value: true,
          message: t('You must select profile visibility setting to continue'),
        },
      }}
    />
  );
};
